import { useState, useEffect } from 'react';
import { D, st, apiPost } from '../constants';

const MASCOT = '/mascot.png';

const FEATURES = [
  { icon: '🤖', title: 'AI-Powered Bidding', desc: 'Describe a job out loud or type it in — get a full itemized bid with materials, labor, and permits in seconds.' },
  { icon: '📋', title: 'Estimates & Invoicing', desc: 'Create Fixed Price or Time & Material estimates. One-click approval auto-generates invoices and kicks off the job.' },
  { icon: '🚚', title: 'Truck Stock & Inventory', desc: 'Track what is on every truck, deduct stock as it is used, and never run a job short on material.' },
  { icon: '⚡', title: 'Riser Diagram Builder', desc: 'Drag-to-wire riser diagrams with NEC-driven wire, conduit, and grounding sizing built in.' },
  { icon: '📖', title: 'NEC Code Lookup', desc: 'AI-assisted answers to code questions in the field, with quick-reference tables for common circuits.' },
  { icon: '🧾', title: 'Scan Receipts', desc: 'Snap a photo of a supply house receipt and itemize it into job costs, expenses, or truck stock instantly.' },
  { icon: '🏪', title: 'Supply House Pricing', desc: 'Import price lists from your supply house via PDF. Material costs auto-populate into every bid.' },
  { icon: '📊', title: 'Bid Accuracy Tracking', desc: 'Compare estimated vs actual hours on completed jobs so every bid gets sharper over time.' },
];

const PLANS = [
  { name: 'Solo', price: '$49.99', sub: '1-2 seats', features: ['AI bidding and invoicing', 'Permits and NEC lookup', 'Field tools'], highlight: false },
  { name: 'Crew', price: '$99.99', sub: 'up to 6 seats', features: ['Everything in Solo', 'Payroll included', 'Truck stock and inventory'], highlight: true },
  { name: 'Team', price: '$179.99', sub: 'up to 12 seats', features: ['Everything in Crew', 'Priority support', 'Higher AI bid quota'], highlight: false },
  { name: 'Custom', price: 'Contact Us', sub: '12+ seats', features: ['Volume pricing', 'Dedicated onboarding'], highlight: false },
];

const STEPS = [
  { num: '1', title: 'Describe the job', desc: 'Voice or text — AI generates a complete bid with line items, labor, and materials.' },
  { num: '2', title: 'Send the estimate', desc: 'Client gets a clean estimate link. One-click approval kicks off the job automatically.' },
  { num: '3', title: 'Get paid', desc: 'Invoice auto-generates on approval. Accept deposits, track payments, close jobs.' },
];

const SIZE_OPTIONS = ['1-2 (Solo)', '3-6 (Crew)', '7-12 (Team)', '12+ (Custom)'];

function BetaModal({ onClose, onTerms, onPrivacy }) {
  const [form, setForm] = useState({ name:'', email:'', company:'', size:'', location:'', years:'', agreed:false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { width:'100%', boxSizing:'border-box', padding:'10px 12px', borderRadius:8, border:'1px solid '+D.border, background:D.bg, color:D.text, fontSize:14, outline:'none', marginBottom:12 };
  const lbl = { fontSize:12, color:D.muted, fontWeight:600, marginBottom:4, display:'block' };
  const submit = async () => {
    if (!form.name||!form.email||!form.company||!form.size||!form.location||!form.years) { setError('Please fill out all fields.'); return; }
    if (!form.agreed) { setError('You must agree to the Terms of Service and Privacy Policy.'); return; }
    setLoading(true); setError('');
    try { await apiPost('/beta/request', {name:form.name,email:form.email,company:form.company,size:form.size,location:form.location,years:form.years}); setSent(true); }
    catch(e) { setError(e.message||'Something went wrong. Try again.'); }
    setLoading(false);
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:D.card,borderRadius:16,border:'1px solid '+D.border,padding:32,width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div><div style={{fontWeight:800,fontSize:20}}>Request Beta Access</div><div style={{fontSize:13,color:D.muted,marginTop:4}}>We will review your request and send you an invite.</div></div>
          <button onClick={onClose} style={{background:'none',border:'none',color:D.muted,fontSize:22,cursor:'pointer',padding:4}}>&#x2715;</button>
        </div>
        {sent ? (
          <div style={{textAlign:'center',padding:'32px 0'}}>
            <div style={{fontSize:48,marginBottom:16}}>&#x26A1;</div>
            <div style={{fontWeight:700,fontSize:18,marginBottom:8}}>Request received!</div>
            <div style={{color:D.muted,fontSize:14,marginBottom:24}}>We will review your request and reach out soon.</div>
            <button onClick={onClose} style={{...st.btn('primary'),marginLeft:0,padding:'10px 32px'}}>Close</button>
          </div>
        ) : (
          <div>
            <label style={lbl}>Full Name</label>
            <input style={inp} value={form.name} onChange={e=>set('name',e.target.value)} placeholder='Jane Smith'/>
            <label style={lbl}>Email Address</label>
            <input style={inp} type='email' value={form.email} onChange={e=>set('email',e.target.value)} placeholder='jane@yourcompany.com'/>
            <label style={lbl}>Company Name</label>
            <input style={inp} value={form.company} onChange={e=>set('company',e.target.value)} placeholder='Smith Electric LLC'/>
            <label style={lbl}>Company Size</label>
            <select style={inp} value={form.size} onChange={e=>set('size',e.target.value)}>
              <option value=''>Select size...</option>
              {SIZE_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}
            </select>
            <label style={lbl}>Location (City, State)</label>
            <input style={inp} value={form.location} onChange={e=>set('location',e.target.value)} placeholder='Tampa, FL'/>
            <label style={lbl}>Years in Business</label>
            <input style={inp} value={form.years} onChange={e=>set('years',e.target.value)} placeholder='5'/>
            <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:16}}>
              <input type='checkbox' checked={form.agreed} onChange={e=>set('agreed',e.target.checked)} style={{marginTop:3,flexShrink:0}}/>
              <span style={{fontSize:12,color:D.muted,lineHeight:1.6}}>I agree to the <span onClick={onTerms} style={{color:D.accent,cursor:'pointer',textDecoration:'underline'}}>Terms of Service</span> and <span onClick={onPrivacy} style={{color:D.accent,cursor:'pointer',textDecoration:'underline'}}>Privacy Policy</span></span>
            </div>
            {error && <div style={{color:D.danger,fontSize:13,marginBottom:12}}>{error}</div>}
            <button onClick={submit} disabled={loading} style={{...st.btn('primary'),marginLeft:0,width:'100%',padding:'12px 0',fontSize:15}}>{loading?'Submitting...':'Request Access'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Landing({ onLogin, onTerms, onPrivacy }) {
  const [showBetaModal, setShowBetaModal] = useState(false);
  const openBeta = () => setShowBetaModal(true);
  useEffect(() => { apiPost('/analytics/event', { type: 'landing_view' }).catch(() => {}); }, []);
  return (
    <div style={{ minHeight: '100vh', background: D.bg, color: D.text, fontFamily: "'Barlow',sans-serif", overflowX: 'hidden' }}>

      {showBetaModal && <BetaModal onClose={()=>setShowBetaModal(false)} onTerms={onTerms} onPrivacy={onPrivacy}/>}
      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: D.accent, letterSpacing: 0.5 }}>⚡ AmpBooks</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onLogin} style={{ ...st.btn(), marginLeft: 0 }}>Log In</button>
          <button onClick={openBeta} style={{ ...st.btn('primary'), marginLeft: 0 }}>Request Beta Access</button>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 32px 60px', display: 'flex', alignItems: 'center', gap: 48, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 280 }}>
          <div style={{ display: 'inline-block', background: D.accent+'22', color: D.accent, fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 18, letterSpacing: 1 }}>BUILT FOR ELECTRICAL CONTRACTORS</div>
          <h1 style={{ fontSize: 'clamp(30px,5vw,50px)', fontWeight: 900, lineHeight: 1.1, marginBottom: 20, color: D.text }}>
            Run your electrical business smarter, faster, <span style={{ color: D.accent }}>better.</span>
          </h1>
          <p style={{ fontSize: 16, color: D.muted, lineHeight: 1.7, marginBottom: 32, maxWidth: 480 }}>
            AI-powered bidding, estimates, invoicing, truck stock, payroll, and NEC code lookup — built specifically for electricians, not retrofitted from generic software.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={openBeta} style={{ ...st.btn('primary'), marginLeft: 0, padding: '14px 32px', fontSize: 16, borderRadius: 10 }}>Request Beta Access</button>
            <button onClick={onLogin} style={{ ...st.btn(), marginLeft: 0, padding: '14px 32px', fontSize: 16, borderRadius: 10 }}>Log In</button>
          </div>
          <div style={{ fontSize: 12, color: D.muted, marginTop: 12 }}>Early access · Limited spots available</div>
        </div>
        <div style={{ flex: '0 1 340px', textAlign: 'center' }}>
          <img src={MASCOT} alt="AmpBooks Mascot" style={{ width: '100%', maxWidth: 320, filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.6))' }} />
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ background: D.card, borderTop: '1px solid '+D.border, borderBottom: '1px solid '+D.border, padding: '22px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'center', gap: 52, flexWrap: 'wrap' }}>
          {[['⚡','AI bids in seconds'],['📋','Estimate to invoice in 1 click'],['🔒','Secure and reliable'],['📱','Works on any device']].map(([icon,label])=>(
            <div key={label} style={{ fontSize: 13, color: D.muted, fontWeight: 600 }}><span style={{ marginRight: 6 }}>{icon}</span>{label}</div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '70px 32px 40px' }}>
        <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>How it works</h2>
        <p style={{ textAlign: 'center', color: D.muted, fontSize: 14, marginBottom: 48 }}>From job description to paid invoice in three steps.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 24 }}>
          {STEPS.map(s=>(
            <div key={s.num} style={{ textAlign: 'center', padding: '32px 24px', background: D.card, borderRadius: 14, border: '1px solid '+D.border }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: D.accent+'22', color: D.accent, fontSize: 22, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>{s.num}</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: D.muted, lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '50px 32px 70px' }}>
        <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Everything you need on the job</h2>
        <p style={{ textAlign: 'center', color: D.muted, fontSize: 14, marginBottom: 40 }}>No generic software retrofitted for electrical. AmpBooks is built ground-up for electricians.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 }}>
          {FEATURES.map(f=>(
            <div key={f.title} style={{ padding: 24, background: D.card, borderRadius: 12, border: '1px solid '+D.border }}>
              <div style={{ fontSize: 30, marginBottom: 12 }}>{f.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: D.muted, lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div style={{ background: D.card, borderTop: '1px solid '+D.border }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '70px 32px' }}>
          <h2 style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Simple, honest pricing</h2>
          <p style={{ textAlign: 'center', color: D.muted, fontSize: 14, marginBottom: 40 }}>Beta access is free. Pick a plan when we go live.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
            {PLANS.map(p=>(
              <div key={p.name} style={{ padding: 28, background: p.highlight ? D.accent+'11' : D.bg, borderRadius: 14, border: p.highlight ? '2px solid '+D.accent : '1px solid '+D.border, textAlign: 'center', position: 'relative' }}>
                {p.highlight && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: D.accent, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 12px', borderRadius: 20 }}>MOST POPULAR</div>}
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{p.name}</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: p.highlight ? D.accent : D.text, marginBottom: 2 }}>{p.price}</div>
                <div style={{ fontSize: 11, color: D.muted, marginBottom: 20 }}>{p.price === 'Contact Us' ? p.sub : p.sub+' / month'}</div>
                <div style={{ textAlign: 'left', marginBottom: 24 }}>
                  {p.features.map(f=>(
                    <div key={f} style={{ fontSize: 13, color: D.muted, padding: '5px 0', display: 'flex', gap: 8 }}>
                      <span style={{ color: D.success, flexShrink: 0 }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                <button onClick={p.price === 'Contact Us' ? undefined : openBeta} style={{ ...st.btn(p.highlight ? 'primary' : undefined), marginLeft: 0, width: '100%', padding: '10px 0' }}>
                  {p.price === 'Contact Us' ? 'Contact Us' : 'Request Beta Access'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={{ textAlign: 'center', padding: '80px 32px' }}>
        <img src={MASCOT} alt="AmpBooks" style={{ width: 110, marginBottom: 24, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))' }} />
        <h2 style={{ fontSize: 30, fontWeight: 900, marginBottom: 12, color: '#ffffff' }}>Ready to run a tighter operation?</h2>
        <p style={{ fontSize: 15, color: D.muted, maxWidth: 480, margin: '0 auto 28px' }}>Join electrical contractors using AmpBooks to bid faster, invoice smarter, and track every dollar.</p>
        <button onClick={openBeta} style={{ ...st.btn('primary'), marginLeft: 0, padding: '15px 40px', fontSize: 17, borderRadius: 12 }}>Request Beta Access</button>
        <div style={{ fontSize: 12, color: D.muted, marginTop: 12 }}>Early access · Limited spots available</div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid '+D.border, padding: '24px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: D.muted }}>2026 AmpBooks LLC · Built for electrical contractors · support@ampbooks.io</div>
        <div style={{ marginTop: 8, fontSize: 12, color: D.muted }}>
          <span onClick={onTerms} style={{ cursor: 'pointer', textDecoration: 'underline', marginRight: 16 }}>Terms of Service</span>
          <span onClick={onPrivacy} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>
        </div>
      </div>

    </div>
  );
}
